import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import download from 'downloadjs';
import { withStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import Report from '@material-ui/icons/LibraryBooks';
import Button from 'components/CustomButtons/Button.jsx';
import Card from 'components/Card/Card.jsx';
import CardBody from 'components/Card/CardBody.jsx';
import CardHeader from 'components/Card/CardHeader.jsx';
import CardIcon from 'components/Card/CardIcon.jsx';
import Typography from '@material-ui/core/Typography';
import GridContainer from 'components/Grid/GridContainer.jsx';
import GridItem from 'components/Grid/GridItem.jsx';
import { DatePicker, DateTimePicker } from 'material-ui-pickers';
import {
  getExcelReport,
  getSkidataAnalytics,
} from '../schema/skidata-schema.graphql';
import { compose, withApollo } from 'react-apollo';
import { withSearch } from '../../../context/SearchResultsContext';
import { withSnackbar } from '../../snackbar/SnackbarContext';
import TicketsGraph from '../../reports/components/TicketsGraph';
import TotalRevenue from '../../reports/components/TotalRevenue';
import TotalTicketsSold from '../../reports/components/TotalTicketsSold';
import TicketPoolDonut from './TicketPoolDonut';
import TotalTicketsCanceled from '../../reports/components/TotalTicketsCanceled';
import zbagTicketTypes from './ticketTypes';
import localization from '../../../i18n/localization';
import selectStyles from 'assets/jss/material-dashboard-pro-react/customSelectStyle.jsx';
import ReportSummary, {
  SummaryItem,
  SummaryTotal,
} from '../../reports/components/ReportSummary';
import Merchant from './merchant';
import allPools from './pools';
import allPersonTypes from './personTypes';
import allPaymentTypes from './paymentTypes';
import allTicketLoadings from './ticketLoadings';

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing.unit * 3,
  },
});

class ZbagReport extends React.Component {
  state = {
    page: 0,
    rowsPerPage: 5,
    validFrom: null,
    validTo: null,
    transactionFrom: moment()
      .startOf('month')
      .toDate(),
    transactionTo: moment().toDate(),
    ticketTypes: [],
    pools: [],
    personTypes: [],
    paymentTypes: [],
    ticketLoadings: [],
  };

  componentDidMount() {
    this.onSearchRequest();
  }

  handleDateChange = field => date => {
    this.setState({ [field]: date });
  };

  onTicketTypesChange = event => {
    this.setState({ ticketTypes: event.target.value });
  };

  onPoolsChange = event => {
    this.setState({ pools: event.target.value });
  };

  onPersonTypesChange = event => {
    this.setState({ personTypes: event.target.value });
  };

  onPaymentTypesChange = event => {
    this.setState({ paymentTypes: event.target.value });
  };

  onTicketLoadingsChange = event => {
    this.setState({ ticketLoadings: event.target.value });
  };

  onSaveReport = async () => {
    const {
      validFrom,
      validTo,
      transactionFrom,
      transactionTo,
      ticketTypes,
      pools,
      personTypes,
      paymentTypes,
      ticketLoadings,
    } = this.state;
    const { client, addNotification } = this.props;

    this.setState({ loading: true });
    try {
      const { data } = await client.query({
        query: getExcelReport,
        variables: {
          transactionFrom: transactionFrom ? transactionFrom : undefined,
          transactionTo: transactionTo ? transactionTo : undefined,
          validFrom: validFrom
            ? moment(validFrom)
                .startOf('day')
                .toISOString()
            : undefined,
          validTo: validTo
            ? moment(validTo)
                .endOf('day')
                .toISOString()
            : undefined,
          ticketTypes:
            ticketTypes.length > 0 ? ticketTypes : Object.keys(zbagTicketTypes),
          merchant: Merchant.ZBAG,
          pools: pools.length > 0 ? pools : Object.keys(allPools),
          personTypes: personTypes.length > 0 ? personTypes : undefined,
          paymentTypes: paymentTypes.length > 0 ? paymentTypes : undefined,
          ticketLoadings:
            ticketLoadings.length > 0 ? ticketLoadings : undefined,
        },
      });
      download(
        'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
          data.report,
        'App-Skidata-Zbag-Report.xlsx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      );
    } catch (error) {
      addNotification('danger', 'Unerwarteter Fehler aufgetreten');
      // eslint-disable-next-line no-console
      console.error(error);
    }
    this.setState({ loading: false });
  };

  onSearchRequest = async () => {
    const {
      validFrom,
      validTo,
      transactionFrom,
      transactionTo,
      ticketTypes,
      pools,
      personTypes,
      paymentTypes,
      ticketLoadings,
    } = this.state;
    const { setSearchResults, addNotification, client } = this.props;

    this.setState({ loading: true });
    try {
      const { data } = await client.query({
        fetchPolicy: 'network-only',
        query: getSkidataAnalytics,
        variables: {
          transactionFrom: transactionFrom ? transactionFrom : undefined,
          transactionTo: transactionTo ? transactionTo : undefined,
          validFrom: validFrom
            ? moment(validFrom)
                .startOf('day')
                .toISOString()
            : undefined,
          validTo: validTo
            ? moment(validTo)
                .endOf('day')
                .toISOString()
            : undefined,
          ticketTypes:
            ticketTypes.length > 0 ? ticketTypes : Object.keys(zbagTicketTypes),
          merchant: Merchant.ZBAG,
          pools: pools.length > 0 ? pools : Object.keys(allPools),
          personTypes: personTypes.length > 0 ? personTypes : undefined,
          paymentTypes: paymentTypes.length > 0 ? paymentTypes : undefined,
          ticketLoadings:
            ticketLoadings.length > 0 ? ticketLoadings : undefined,
        },
      });

      setSearchResults('', data.analytics);
    } catch (error) {
      addNotification('danger', 'Unerwarteter Fehler aufgetreten');
      console.error(error);
    }
    this.setState({ loading: false });
  };

  render() {
    // eslint-disable-next-line react/prop-types
    const { classes, getSearchResults } = this.props;
    const {
      loading,
      validFrom,
      validTo,
      transactionFrom,
      transactionTo,
      ticketTypes,
      pools,
      personTypes,
      paymentTypes,
      ticketLoadings,
    } = this.state;

    const analytics = getSearchResults().results;

    return (
      <div className={classes.root}>
        <Card>
          <CardHeader icon stats>
            <CardIcon color="rose">
              <Report />
            </CardIcon>
            <div
              style={{
                marginTop: 80,
                marginBottom: 25,
                display: 'flex',
                width: '100%',
              }}
            >
              <Typography variant="headline" color="textSecondary">
                ZBAG Report
              </Typography>
            </div>
          </CardHeader>
          <CardBody>
            <div
              style={{
                marginBottom: 40,
                marginTop: 0,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div style={{ maxWidth: 140, marginRight: 20 }}>
                <DateTimePicker
                  ampm={false}
                  cancelLabel={'Abbrechen'}
                  clearLabel={'L\u00d6schen'}
                  label={'Transaktionen von'}
                  variant="outlined"
                  value={transactionFrom}
                  onChange={this.handleDateChange('transactionFrom')}
                  format={'DD.MM.YYYY HH:mm'}
                  clearable
                  InputProps={{ style: { fontSize: 12 } }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
              </div>
              <div style={{ maxWidth: 140, marginRight: 20 }}>
                <DateTimePicker
                  ampm={false}
                  cancelLabel={'Abbrechen'}
                  clearLabel={'L\u00d6schen'}
                  label={'Transaktionen bis'}
                  variant="outlined"
                  value={transactionTo}
                  onChange={this.handleDateChange('transactionTo')}
                  format={'DD.MM.YYYY HH:mm'}
                  clearable
                  InputProps={{ style: { fontSize: 12 } }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
              </div>
              <div style={{ marginRight: 20, maxWidth: 100 }}>
                <DatePicker
                  cancelLabel={'Abbrechen'}
                  clearLabel={'L\u00d6schen'}
                  label={'G\u00fcltig von'}
                  variant="outlined"
                  value={validFrom}
                  onChange={this.handleDateChange('validFrom')}
                  format={'DD.MM.YYYY'}
                  clearable
                  InputProps={{ style: { fontSize: 12 } }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
              </div>
              <div style={{ maxWidth: 100, marginRight: 20 }}>
                <DatePicker
                  cancelLabel={'Abbrechen'}
                  clearLabel={'L\u00d6schen'}
                  label={'G\u00fcltig bis'}
                  variant="outlined"
                  value={validTo}
                  onChange={this.handleDateChange('validTo')}
                  format={'DD.MM.YYYY'}
                  clearable
                  InputProps={{ style: { fontSize: 12 } }}
                  InputLabelProps={{ style: { fontSize: 12 } }}
                />
              </div>
              <div style={{ width: 150, marginRight: 20 }}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  // className={classes.selectFormControl}
                >
                  <InputLabel htmlFor="ticket-select" style={{ fontSize: 12 }}>
                    Artikel
                  </InputLabel>
                  <Select
                    variant="outlined "
                    multiple
                    style={{ height: 50, fontSize: 12 }}
                    input={
                      <OutlinedInput
                        labelWidth={35}
                        name="age"
                        id="outlined-age-simple"
                      />
                    }
                    value={ticketTypes}
                    onChange={this.onTicketTypesChange}
                    inputProps={{
                      name: 'ticket-select',
                      id: 'ticket-select',
                    }}
                  >
                    {Object.keys(zbagTicketTypes).map(ticketType => (
                      <MenuItem
                        key={ticketType}
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected,
                        }}
                        value={ticketType}
                      >
                        {localization.de.ticketType[ticketType]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
            <div
              style={{
                marginBottom: 40,
                marginTop: 0,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <div style={{ width: 165, marginRight: 20 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="pool-select" style={{ fontSize: 12 }}>
                    Pool
                  </InputLabel>
                  <Select
                    variant="outlined "
                    multiple
                    style={{ height: 50, fontSize: 12 }}
                    input={
                      <OutlinedInput
                        labelWidth={35}
                        name="age"
                        id="outlined-age-simple"
                      />
                    }
                    value={pools}
                    onChange={this.onPoolsChange}
                    inputProps={{
                      name: 'pool-select',
                      id: 'pool-select',
                    }}
                  >
                    {Object.keys(allPools).map(pool => (
                      <MenuItem
                        key={pool}
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected,
                        }}
                        value={pool}
                      >
                        {localization.de.pools[pool]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div style={{ width: 160, marginRight: 20 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="pool-select" style={{ fontSize: 12 }}>
                    Person Type
                  </InputLabel>
                  <Select
                    variant="outlined "
                    multiple
                    style={{ height: 50, fontSize: 12 }}
                    input={
                      <OutlinedInput
                        labelWidth={35}
                        name="age"
                        id="outlined-age-simple"
                      />
                    }
                    value={personTypes}
                    onChange={this.onPersonTypesChange}
                    inputProps={{
                      name: 'personType-select',
                      id: 'personType-select',
                    }}
                  >
                    {Object.keys(allPersonTypes).map(personType => (
                      <MenuItem
                        key={personType}
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected,
                        }}
                        value={personType}
                      >
                        {localization.de.personTypes[personType]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div style={{ width: 160, marginRight: 20 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="pool-select" style={{ fontSize: 12 }}>
                    Payment Type
                  </InputLabel>
                  <Select
                    variant="outlined "
                    multiple
                    style={{ height: 50, fontSize: 12 }}
                    input={
                      <OutlinedInput
                        labelWidth={35}
                        name="age"
                        id="outlined-age-simple"
                      />
                    }
                    value={paymentTypes}
                    onChange={this.onPaymentTypesChange}
                    inputProps={{
                      name: 'paymentType-select',
                      id: 'paymentType-select',
                    }}
                  >
                    {Object.keys(allPaymentTypes).map(paymentType => (
                      <MenuItem
                        key={paymentType}
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected,
                        }}
                        value={paymentType}
                      >
                        {localization.de.paymentTypes[paymentType]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div style={{ width: 165, marginRight: 20 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel htmlFor="pool-select" style={{ fontSize: 12 }}>
                    Ticket Loading
                  </InputLabel>
                  <Select
                    variant="outlined "
                    multiple
                    style={{ height: 50, fontSize: 12 }}
                    input={
                      <OutlinedInput
                        labelWidth={35}
                        name="age"
                        id="outlined-age-simple"
                      />
                    }
                    value={ticketLoadings}
                    onChange={this.onTicketLoadingsChange}
                    inputProps={{
                      name: 'ticketLoading-select',
                      id: 'ticketLoading-select',
                    }}
                  >
                    {Object.keys(allTicketLoadings).map(ticketLoading => (
                      <MenuItem
                        key={ticketLoading}
                        classes={{
                          root: classes.selectMenuItem,
                          selected: classes.selectMenuItemSelected,
                        }}
                        value={allTicketLoadings[ticketLoading]}
                      >
                        {localization.de.ticketLoadings[ticketLoading]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
            <div />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 40,
              }}
            >
              <Button
                style={{ marginRight: 10 }}
                color="info"
                onClick={this.onSearchRequest}
                disabled={loading}
              >
                Aktualisieren
              </Button>
              <Button
                color="primary"
                onClick={this.onSaveReport}
                disabled={loading}
              >
                Excel Export
              </Button>
            </div>
            {analytics ? (
              <GridContainer>
                <GridItem xs={12} sm={6} md={4} lg={4}>
                  <TotalRevenue
                    revenue={
                      analytics.totalTicketsPerPool.reduce(
                        (revenue, ticket) => revenue + ticket.totalRevenue,
                        0
                      ) +
                      analytics.totalTicketsPerPool.reduce(
                        (subTotal, ticket) =>
                          subTotal +
                          ticket.addonsTotalRevenue -
                          ticket.addonsTotalStorno,
                        0
                      ) +
                      analytics.totalRevenueCashback -
                      analytics.totalTicketsPerPool.reduce(
                        (storno, ticket) => storno + ticket.totalStorno,
                        0
                      )
                    }
                  />
                </GridItem>
                <GridItem xs={12} sm={6} md={4} lg={4}>
                  <TotalTicketsSold
                    ticketCount={analytics.totalTicketsPerPool.reduce(
                      (soldCount, ticket) => soldCount + ticket.soldCount,
                      0
                    )}
                  />
                </GridItem>
                <GridItem xs={12} sm={6} md={4} lg={4}>
                  <TotalTicketsCanceled
                    ticketCount={analytics.totalTicketsPerPool.reduce(
                      (soldCount, ticket) => soldCount + ticket.stornoCount,
                      0
                    )}
                  />
                </GridItem>
                <GridItem xs={12} sm={6} md={12} lg={6}>
                  <TicketsGraph
                    ticketCountPerDay={analytics.ticketsSoldPerDay}
                  />
                </GridItem>
                <GridItem xs={12} sm={6} md={12} lg={6}>
                  <TicketPoolDonut
                    ticketsCount={analytics.totalTicketsPerPool.map(tickets => {
                      return { pool: tickets.pool, count: tickets.soldCount };
                    })}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={12} lg={12}>
                  <div style={{ marginTop: 20, marginBottom: 20 }}>
                    <Typography variant="title" color="textSecondary">
                      Zusammenfassung
                    </Typography>
                  </div>
                </GridItem>
                {analytics.totalTicketsPerPool.map((tickets, key) => {
                  return (
                    <GridItem
                      xs={12}
                      sm={12}
                      md={6}
                      lg={6}
                      key={key}
                      style={{ marginBottom: 20 }}
                    >
                      <ReportSummary title={tickets.pool}>
                        <SummaryItem
                          text="Tickets"
                          value={tickets.totalRevenue}
                        />
                        <SummaryItem
                          variant="storno"
                          text="Storno Tickets"
                          value={-tickets.totalStorno}
                        />
                        <SummaryItem
                          variant="subtotal"
                          text="Total Tickets"
                          value={tickets.totalRevenue - tickets.totalStorno}
                        />
                        <SummaryItem
                          text="Versicherungen"
                          value={tickets.addonsTotalRevenue}
                        />
                        <SummaryItem
                          variant="storno"
                          text="Storno Versicherungen"
                          value={-tickets.addonsTotalStorno}
                        />
                        <SummaryItem
                          variant="subtotal"
                          text="Total Versicherungen"
                          value={
                            tickets.addonsTotalRevenue -
                            tickets.addonsTotalStorno
                          }
                        />
                        {tickets.pool === allPools.ZERMATT ? (
                          <React.Fragment>
                            <SummaryItem
                              text="Cashback"
                              value={analytics.totalRevenueCashback}
                            />
                            <SummaryItem
                              variant="subtotal"
                              text="Total Cashback"
                              value={analytics.totalRevenueCashback}
                            />
                            <SummaryItem
                              text="Gebühren"
                              value={tickets.revenueFees}
                            />
                            <SummaryItem
                              variant="storno"
                              text="Storno Gebühren"
                              value={-tickets.stornoFees}
                            />
                            <SummaryItem
                              variant="subtotal"
                              text="Total Gebühren"
                              value={tickets.revenueFees - tickets.stornoFees}
                            />
                          </React.Fragment>
                        ) : null}
                        <SummaryTotal
                          text="Total"
                          value={tickets.revenue - tickets.storno}
                        />
                      </ReportSummary>
                    </GridItem>
                  );
                })}

                <GridItem
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  style={{ marginTop: 30 }}
                />
                <GridItem xs={12} sm={12} md={6} lg={6}>
                  <ReportSummary title="Gesamtsummen">
                    <SummaryItem
                      text="Tickets"
                      value={analytics.totalTicketsPerPool.reduce(
                        (revenue, ticket) => revenue + ticket.totalRevenue,
                        0
                      )}
                    />
                    <SummaryItem
                      variant="storno"
                      text="Storno Tickets"
                      value={
                        -analytics.totalTicketsPerPool.reduce(
                          (storno, ticket) => storno + ticket.totalStorno,
                          0
                        )
                      }
                    />
                    <SummaryItem
                      variant="subtotal"
                      text="Total Tickets"
                      value={analytics.totalTicketsPerPool.reduce(
                        (subTotal, ticket) =>
                          subTotal + ticket.totalRevenue - ticket.totalStorno,
                        0
                      )}
                    />
                    <SummaryItem
                      text="Versicherungen"
                      value={analytics.totalTicketsPerPool.reduce(
                        (subTotal, ticket) =>
                          subTotal + ticket.addonsTotalRevenue,
                        0
                      )}
                    />
                    <SummaryItem
                      variant="storno"
                      text="Storno Versicherungen"
                      value={
                        -analytics.totalTicketsPerPool.reduce(
                          (subTotal, ticket) =>
                            subTotal + ticket.addonsTotalStorno,
                          0
                        )
                      }
                    />
                    <SummaryItem
                      variant="subtotal"
                      text="Total Versicherungen"
                      value={analytics.totalTicketsPerPool.reduce(
                        (subTotal, ticket) =>
                          subTotal +
                          ticket.addonsTotalRevenue -
                          ticket.addonsTotalStorno,
                        0
                      )}
                    />
                    <SummaryItem
                      text="Cashback"
                      value={analytics.totalRevenueCashback}
                    />
                    <SummaryItem
                      variant="subtotal"
                      text="Total Cashback"
                      value={analytics.totalRevenueCashback}
                    />
                    <SummaryItem
                      text="Gebühren"
                      value={analytics.totalRevenueFees}
                    />
                    <SummaryItem
                      variant="storno"
                      text="Storno Gebühren"
                      value={-analytics.totalStornoFees}
                    />
                    <SummaryItem
                      variant="subtotal"
                      text="Total Gebühren"
                      value={
                        analytics.totalRevenueFees - analytics.totalStornoFees
                      }
                    />
                    <SummaryTotal
                      text="Total"
                      value={
                        analytics.totalTicketsPerPool.reduce(
                          (revenue, ticket) => revenue + ticket.totalRevenue,
                          0
                        ) +
                        analytics.totalTicketsPerPool.reduce(
                          (subTotal, ticket) =>
                            subTotal +
                            ticket.addonsTotalRevenue -
                            ticket.addonsTotalStorno,
                          0
                        ) +
                        analytics.totalRevenueCashback -
                        analytics.totalTicketsPerPool.reduce(
                          (storno, ticket) => storno + ticket.totalStorno,
                          0
                        )
                      }
                    />
                  </ReportSummary>
                  <ReportSummary />
                </GridItem>
              </GridContainer>
            ) : null}
          </CardBody>
        </Card>

        <GridContainer />
      </div>
    );
  }
}

ZbagReport.propTypes = {
  classes: PropTypes.object.isRequired,
};

const enhance = compose(
  withSnackbar,
  withSearch('skidata-reports'),
  withStyles({ ...styles, ...selectStyles }),
  withApollo
);
export default enhance(ZbagReport);



// WEBPACK FOOTER //
// ./src/views/skidata/components/ZbagReport.jsx